<template>
    <div class="main-content" ref="content">
        <component-plugin
            capability="header"
            v-bind:logo="brandLogo"
            v-show="modelConfigured"
            v-on:hook:updated="resize"
        />
        <retail-configurator v-bind:size="configuratorSize" />
        <div class="controls-container" v-show="modelConfigured" ref="controls">
            <slot name="thumbnails" />
            <retail-pickers v-bind:smooth="false" v-show="supportsCustomization" ref="pickers">
                <retail-personalization v-show="supportsPersonalization" />
            </retail-pickers>
            <div class="action-buttons clearfix">
                <retail-size v-show="supportsSize" />
                <confirmation v-bind:order-url="orderUrl" />
            </div>
            <slot name="designs" />
        </div>
    </div>
</template>

<style scoped>
.main-content {
    box-sizing: border-box;
    display: block;
    height: 100%;
    margin: 0px auto 0px auto;
    max-width: 1080px;
    overflow: hidden;
    padding: 50px 50px 50px 50px;
    text-align: center;
    width: 100%;
}

.main-content .action-buttons {
    border-bottom: 1px solid #f5f5f5;
    line-height: 50px;
    margin: 2vh -4vw 1vh -4vw;
    padding: 0px 10vw 4vh 10vw;
}

.main-content .action-buttons > .button {
    border: 2px solid #000000;
    border-radius: 24px 24px 24px 24px;
    font-size: 15px;
}

@media screen and (max-height: 1340px) {

    .main-content {
        padding: 0px 20px 0px 20px;
    }

    .main-content > .action-buttons {
        border-bottom-width: 0px;
        line-height: 40px;
        margin: 1vh -4vw 3vh -4vw;
        padding: 0px 10vw 0px 10vw;
    }
}

@media screen and (max-height: 1620px) {

    .main-content {
        overflow-y: auto;
    }
}

.main-content ::v-deep .button {
    cursor: pointer;
    outline: none;
}

.main-content ::v-deep .button.button-primary,
.main-content ::v-deep .button.button-apply {
    background-color: #000000;
    border: none;
    border-radius: 12px 12px 12px 12px;
    color: #ffffff;
    font-size: 20px;
    font-weight: 500;
    height: 50px;
    line-height: 50px;
    transition: background-color 0.25s ease-in-out, color 0.25s ease-in-out;
    width: 300px;
}

.main-content ::v-deep .button.button-primary:hover,
.main-content ::v-deep .button.button-apply:hover {
    background-color: #6d6d6d;
}

.main-content ::v-deep .button.button-primary.invalid,
.main-content ::v-deep .button.button-apply.invalid {
    background-color: #cccccc;
    pointer-events: none;
}

.main-content ::v-deep .button.button-primary:active,
.main-content ::v-deep .button.button-apply:active {
    color: rgba(255, 255, 255, 0.5);
}

.main-content ::v-deep .button.button-primary.loading,
.main-content ::v-deep .button.button-apply.loading {
    background-color: #cccccc;
}

@media screen and (max-height: 1620px) {

    .main-content > .retail-configurator ::v-deep .configurator {
        margin-top: -6vh;
    }
}
</style>

<script>
import { deviceMixin } from "ripe-commons-pluginus";

export const MainContent = {
    name: "main-content",
    mixins: [deviceMixin],
    props: {
        brandLogo: {
            type: String,
            mandatory: true
        },
        orderUrl: {
            type: String,
            mandatory: true
        }
    },
    data: function() {
        return {
            /**
             * The size used by the configurator.
             */
            configuratorSize: 0
        };
    },
    computed: {
        supportsCustomization() {
            if (!this.modelConfigured) return false;
            return this.$store.state.hasCustomization;
        },
        supportsPersonalization() {
            if (!this.modelConfigured) return false;
            return this.$store.state.hasPersonalization;
        },
        supportsSize() {
            if (!this.modelConfigured) return false;
            return this.$store.state.hasSize;
        }
    },
    created: function() {
        this.initDevice();
    },
    mounted: function() {
        this.resize();
        window.addEventListener("resize", this.resize);
    },
    methods: {
        /**
         * Sets the size for the configurator based on the available size and allowed maximum.
         */
        resize() {
            const content = this.$refs.content;
            const header = content.querySelector(".header");

            // Header is still not mounted, nothing to do for now
            if (!header) {
                return;
            }

            const windowHeight = window.innerHeight;
            const headerHeight = header.offsetHeight;
            const controlsHeight = this.$refs.controls.offsetHeight;
            const verticalSpace = windowHeight - headerHeight - controlsHeight;

            const partsPicker = this.$refs.pickers.$refs.pickers.$refs.partsPicker;
            const horizontalSpace = partsPicker.offsetWidth;

            this.configuratorSize = Math.min(verticalSpace, horizontalSpace);
        }
    }
};

export default MainContent;
</script>
